import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box, Typography, Button, CircularProgress, IconButton, Tooltip, Checkbox, FormControlLabel, Dialog,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import MicIcon from "@mui/icons-material/Mic";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ReplayIcon from "@mui/icons-material/Replay";
import WifiIcon from "@mui/icons-material/Wifi";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import DnsIcon from "@mui/icons-material/Dns";
import SecurityIcon from "@mui/icons-material/Security";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

import {
    CHECK_STATUS,
    CHECK_DEFINITIONS,
    NETWORK_THRESHOLDS,
    MIN_DESKTOP_WIDTH,
    BACKEND_TIMEOUT_MS,
    SPEAKER_TEST_SENTENCE,
} from "./systemCheckConstants";
import {
    mapMediaError,
    sampleNetworkLatency,
    sampleMicrophoneEnergy,
    stopAllTracks,
    detectBrowserCapabilities,
    detectDeviceCategory,
    validateCameraTrack,
    validateVideoDimensions,
    validateAudioTrack,
    validateBackendPayload,
    loadFaceApiModels,
    detectFaceInImage,
} from "./systemCheckUtils";

// Adjust this import if your project's Config path differs — this feature does not
// modify Config/config.js, it only reads the already-exported API base URL.
let API_BASE_URL = "";
try {
    // eslint-disable-next-line global-require
    API_BASE_URL = require("../../Config/config").default || "";
} catch {
    API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
}

// Accent blue used for step headings/labels, close to the reference design.
// Primary action buttons keep the project's own brand blue (#1e3a8a) since that's
// what's correct for this app — only the accent text/step-dot color is adjusted.
const ACCENT_BLUE = "#2563eb";
const BRAND_BLUE = "#1e3a8a";

// Minimum average brightness (0-255 scale) for a captured selfie to pass the
// lighting check. This is a plain pixel-brightness analysis, combined with a
// real face-presence check (see detectFaceInImage) — NOT identity verification.
// No image data leaves the browser.
const MIN_SELFIE_BRIGHTNESS = 55;

// Short settle time after video playback starts, before we trust videoWidth/videoHeight
// as proof that real frames are being rendered (some devices report a live track before
// the first frame actually paints).
const VIDEO_FRAME_SETTLE_MS = 250;

const ICONS = {
    browser: SecurityIcon,
    device: DesktopWindowsIcon,
    permissions: SecurityIcon,
    camera: VideocamIcon,
    microphone: MicIcon,
    speaker: VolumeUpIcon,
    network: WifiIcon,
    backend: DnsIcon,
};

function StatusBadge({ status }) {
    const map = {
        [CHECK_STATUS.PENDING]: { label: "Waiting", color: "#94a3b8", Icon: null },
        [CHECK_STATUS.CHECKING]: { label: "Checking…", color: "#3b82f6", Icon: null },
        [CHECK_STATUS.PASSED]: { label: "Ready", color: "#16a34a", Icon: CheckCircleIcon },
        [CHECK_STATUS.WARNING]: { label: "Needs attention", color: "#d97706", Icon: WarningAmberIcon },
        [CHECK_STATUS.FAILED]: { label: "Action required", color: "#dc2626", Icon: ErrorOutlineIcon },
    };
    const cfg = map[status] || map[CHECK_STATUS.PENDING];
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: cfg.color, fontWeight: 600, fontSize: 13 }}>
            {status === CHECK_STATUS.CHECKING ? (
                <CircularProgress size={14} sx={{ color: cfg.color }} />
            ) : cfg.Icon ? (
                <cfg.Icon sx={{ fontSize: 18 }} />
            ) : (
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.color, opacity: 0.6 }} />
            )}
            <span>{cfg.label}</span>
        </Box>
    );
}

/**
 * Pre-Interview System Quality Check.
 *
 * Renders as a centered modal dialog that overlays on top of whatever page is
 * behind it (the existing "Start Interview" instructions page keeps rendering
 * underneath, dimmed by the dialog's backdrop) — the same layout style as the
 * reference "Online Proctoring Setup" popup. It never replaces that page.
 *
 * Self-contained readiness gate. Owns its own temporary camera/mic stream and
 * releases it fully before calling onPassed(), so the existing interview flow
 * acquires its own devices exactly as it does today.
 *
 * UI: two-step flow (Terms -> System Check + Verification Photo).
 * The "verification photo" step captures a frame and runs two checks on it:
 *   1. A real face-presence check (face-api.js tiny face detector) — rejects
 *      photos where a hand, blank wall, or nothing is in frame.
 *   2. A plain brightness check — rejects photos that are too dark.
 * This is presence/lighting validation only — NOT identity matching.
 *
 * Props:
 *  - onPassed: () => void        called when candidate clicks "Proceed to Interview" after checks clear
 *  - candidateName?: string      optional, cosmetic only
 */
export default function SystemQualityCheckPage({ onPassed, candidateName }) {
    const [step, setStep] = useState(1); // 1 = Terms, 2 = System Check + Verification Photo
    const [agreed, setAgreed] = useState(false);

    const [checks, setChecks] = useState(() =>
        CHECK_DEFINITIONS.reduce((acc, c) => {
            acc[c.id] = { ...c, status: CHECK_STATUS.PENDING, message: "" };
            return acc;
        }, {})
    );
    const [activeLabel, setActiveLabel] = useState("Preparing checks…");
    const [micLevel, setMicLevel] = useState(0);
    const [speakerConfirmed, setSpeakerConfirmed] = useState(null); // null | true | false
    const [speakerPlaying, setSpeakerPlaying] = useState(false);

    // Selfie / verification-photo capture state.
    // selfieStatus: idle | checking | captured | too_dark | no_face
    const [selfieDataUrl, setSelfieDataUrl] = useState(null);
    const [selfieStatus, setSelfieStatus] = useState("idle");
    const canvasRef = useRef(null);

    // Face-detection model readiness. `faceModelsLoaded` is only true if the
    // model actually loaded — used to decide whether to run detection at all.
    // `faceCheckAttempted` becomes true once the load attempt finishes either
    // way, so the capture button isn't permanently stuck if loading fails.
    const [faceModelsLoaded, setFaceModelsLoaded] = useState(false);
    const [faceCheckAttempted, setFaceCheckAttempted] = useState(false);

    const streamRef = useRef(null);
    const videoRef = useRef(null);
    const micCleanupRef = useRef(null);
    const mountedRef = useRef(true);
    const runGuardRef = useRef(false); // prevents duplicate runs under StrictMode double-invoke
    const retryRunningRef = useRef(false); // prevents overlapping retries if Retry is clicked rapidly

    const setCheck = useCallback((id, patch) => {
        if (!mountedRef.current) return;
        setChecks((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    }, []);

    // Centralized teardown for every temporary resource this page acquires
    // (camera/mic stream, mic energy analyser, and any in-progress speaker
    // test speech). Safe to call multiple times.
    const cleanupResources = useCallback(() => {
        stopAllTracks(streamRef.current);
        streamRef.current = null;

        if (micCleanupRef.current) {
            micCleanupRef.current();
            micCleanupRef.current = null;
        }

        try {
            window.speechSynthesis && window.speechSynthesis.cancel();
        } catch { /* no-op */ }
    }, []);

    // Load the face-detection model used to validate the verification photo.
    // Runs once, in parallel with the rest of the checks.
    useEffect(() => {
        let cancelled = false;
        loadFaceApiModels().then((ok) => {
            if (cancelled) return;
            setFaceModelsLoaded(ok);
            setFaceCheckAttempted(true);
        });
        return () => { cancelled = true; };
    }, []);

    // ---- Individual checks (validation logic unchanged) -------------------

    const runBrowserCheck = useCallback(async () => {
        setActiveLabel("Checking browser compatibility…");
        setCheck("browser", { status: CHECK_STATUS.CHECKING, message: "" });
        const caps = detectBrowserCapabilities();
        if (!caps.allHardPass) {
            setCheck("browser", {
                status: CHECK_STATUS.FAILED,
                message: "Your browser is missing required capabilities. Please use the latest Chrome or Edge.",
            });
            return false;
        }
        if (!caps.speechRecognition) {
            setCheck("browser", {
                status: CHECK_STATUS.WARNING,
                message: "Voice-response recognition works best in Chrome. Some features may be limited in this browser.",
            });
            return true;
        }
        setCheck("browser", { status: CHECK_STATUS.PASSED, message: "Your browser supports all required features." });
        return true;
    }, [setCheck]);

    const runDeviceCheck = useCallback(async () => {
        setActiveLabel("Checking device and screen…");
        setCheck("device", { status: CHECK_STATUS.CHECKING, message: "" });
        const { category, width } = detectDeviceCategory();
        if (category === "mobile") {
            setCheck("device", {
                status: CHECK_STATUS.WARNING,
                message: "This interview is optimized for a desktop or laptop. You may continue, but some features may be harder to use on mobile.",
            });
            return true;
        }
        if (category === "tablet") {
            setCheck("device", {
                status: CHECK_STATUS.WARNING,
                message: "A desktop or laptop is recommended for the best interview experience.",
            });
            return true;
        }
        if (width < MIN_DESKTOP_WIDTH) {
            setCheck("device", {
                status: CHECK_STATUS.WARNING,
                message: "Your screen is quite narrow. Maximizing your browser window is recommended.",
            });
            return true;
        }
        setCheck("device", { status: CHECK_STATUS.PASSED, message: "Your device and screen meet the requirements." });
        return true;
    }, [setCheck]);

    const runPermissionStateCheck = useCallback(async () => {
        setActiveLabel("Checking permission status…");
        setCheck("permissions", { status: CHECK_STATUS.CHECKING, message: "" });
        if (!navigator.permissions || !navigator.permissions.query) {
            setCheck("permissions", { status: CHECK_STATUS.WARNING, message: "Permission status isn't available in this browser — we'll check directly instead." });
            return true;
        }
        try {
            const [cam, mic] = await Promise.all([
                navigator.permissions.query({ name: "camera" }).catch(() => null),
                navigator.permissions.query({ name: "microphone" }).catch(() => null),
            ]);
            const camState = cam?.state || "unsupported";
            const micState = mic?.state || "unsupported";
            if (camState === "denied" || micState === "denied") {
                setCheck("permissions", {
                    status: CHECK_STATUS.WARNING,
                    message: "Camera or microphone permission is currently blocked. You'll be prompted to allow access in the next step.",
                });
            } else {
                setCheck("permissions", { status: CHECK_STATUS.PASSED, message: "Permissions look good." });
            }
        } catch (err) {
            console.error("System check: permission status query failed", err);
            setCheck("permissions", { status: CHECK_STATUS.WARNING, message: "Couldn't read permission status — we'll check directly instead." });
        }
        return true;
    }, [setCheck]);

    const runCameraAndMicCheck = useCallback(async () => {
        setActiveLabel("Checking camera and microphone…");
        setCheck("camera", { status: CHECK_STATUS.CHECKING, message: "" });
        setCheck("microphone", { status: CHECK_STATUS.CHECKING, message: "" });

        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
                audio: true,
            });
        } catch (err) {
            console.error("System check: getUserMedia failed", err);
            const msg = mapMediaError(err, "camera");
            setCheck("camera", { status: CHECK_STATUS.FAILED, message: msg });
            setCheck("microphone", { status: CHECK_STATUS.FAILED, message: mapMediaError(err, "microphone") });
            return false;
        }

        streamRef.current = stream;
        if (videoRef.current) {
            videoRef.current.srcObject = stream;
            try {
                await videoRef.current.play();
            } catch (err) {
                console.error("System check: video element play() failed", err);
            }
        }

        // --- Camera validation: real track, live, not immediately ended, and
        //     actually producing rendered frames (not just a "live" track state) ---
        const videoTrack = stream.getVideoTracks()[0];
        if (!validateCameraTrack(videoTrack)) {
            setCheck("camera", { status: CHECK_STATUS.FAILED, message: "Your camera stream ended unexpectedly. Please retry." });
        } else {
            let endedEarly = false;
            const onEnded = () => { endedEarly = true; };
            videoTrack.addEventListener("ended", onEnded);
            await new Promise((r) => setTimeout(r, 400));
            videoTrack.removeEventListener("ended", onEnded);
            if (endedEarly) {
                setCheck("camera", { status: CHECK_STATUS.FAILED, message: "Your camera stopped unexpectedly. Please check the device and retry." });
            } else {
                // Give the video element a brief moment to render its first frame,
                // then confirm real dimensions are being produced.
                await new Promise((r) => setTimeout(r, VIDEO_FRAME_SETTLE_MS));
                if (!validateVideoDimensions(videoRef)) {
                    setCheck("camera", { status: CHECK_STATUS.FAILED, message: "We couldn't detect a video image from your camera. Please retry." });
                } else {
                    setCheck("camera", { status: CHECK_STATUS.PASSED, message: "Camera preview looks good." });
                }
            }
        }

        // --- Microphone validation: real energy sampling over a short window ---
        const audioTrack = stream.getAudioTracks()[0];
        if (!validateAudioTrack(audioTrack)) {
            setCheck("microphone", { status: CHECK_STATUS.FAILED, message: "We couldn't access your microphone stream. Please retry." });
        } else {
            setActiveLabel("Listening for microphone input… please say a few words");
            const result = await sampleMicrophoneEnergy(stream);
            micCleanupRef.current = result.cleanup;
            if (result.detected) {
                setCheck("microphone", { status: CHECK_STATUS.PASSED, message: "Microphone input detected." });
            } else {
                setCheck("microphone", {
                    status: CHECK_STATUS.WARNING,
                    message: "We detected a microphone, but no voice input. Speak for a few seconds and try again.",
                });
            }
            setMicLevel(Math.round((result.peakLevel || 0) * 100));
        }

        return true;
    }, [setCheck]);

    const runNetworkCheck = useCallback(async () => {
        setActiveLabel("Measuring connection quality…");
        setCheck("network", { status: CHECK_STATUS.CHECKING, message: "" });
        if (!navigator.onLine) {
            setCheck("network", { status: CHECK_STATUS.FAILED, message: "You appear to be offline. Please check your internet connection and retry." });
            return false;
        }
        const pingUrl = API_BASE_URL ? `${API_BASE_URL}/systemQualityCheck/health` : window.location.origin;
        try {
            const { medianMs } = await sampleNetworkLatency(pingUrl);
            if (medianMs <= NETWORK_THRESHOLDS.GOOD_MS) {
                setCheck("network", { status: CHECK_STATUS.PASSED, message: `Connection looks good (${medianMs}ms).` });
            } else if (medianMs <= NETWORK_THRESHOLDS.FAIR_MS) {
                setCheck("network", { status: CHECK_STATUS.WARNING, message: `Your connection is a little slow (${medianMs}ms). This should still work, but a wired or stronger connection is recommended.` });
            } else {
                setCheck("network", { status: CHECK_STATUS.WARNING, message: `Your connection is slow (${medianMs}ms). Consider moving closer to your router before starting.` });
            }
        } catch (err) {
            console.error("System check: network latency sampling failed", err);
            setCheck("network", { status: CHECK_STATUS.WARNING, message: "We couldn't fully measure your connection speed, but basic connectivity looks fine." });
        }
        return true;
    }, [setCheck]);

    const runBackendCheck = useCallback(async () => {
        setActiveLabel("Connecting to interview service…");
        setCheck("backend", { status: CHECK_STATUS.CHECKING, message: "" });
        if (!API_BASE_URL) {
            setCheck("backend", { status: CHECK_STATUS.WARNING, message: "Could not verify the interview service configuration, but you may continue." });
            return true;
        }
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), BACKEND_TIMEOUT_MS);
        try {
            const res = await fetch(`${API_BASE_URL}/systemQualityCheck/health`, { method: "GET", signal: controller.signal });
            const isValid = await validateBackendPayload(res);
            if (isValid) {
                setCheck("backend", { status: CHECK_STATUS.PASSED, message: "Interview service is reachable." });
            } else {
                setCheck("backend", { status: CHECK_STATUS.WARNING, message: "The interview service responded unexpectedly. You may still be able to continue." });
            }
        } catch (err) {
            console.error("System check: backend health check failed", err);
            setCheck("backend", {
                status: CHECK_STATUS.FAILED,
                message: "We could not connect to the interview service. Check your internet connection and retry.",
            });
            return false;
        } finally {
            clearTimeout(timer);
        }
        return true;
    }, [setCheck]);

    // ---- Orchestration -----------------------------------------------------

    const runAllChecks = useCallback(async () => {
        await runBrowserCheck();
        await runDeviceCheck();
        await runPermissionStateCheck();
        await runCameraAndMicCheck();
        await runNetworkCheck();
        await runBackendCheck();
        if (mountedRef.current) setActiveLabel("Checks complete.");
    }, [runBrowserCheck, runDeviceCheck, runPermissionStateCheck, runCameraAndMicCheck, runNetworkCheck, runBackendCheck]);

    // Speaker branch preserved: retry resets speaker back to PENDING so the
    // candidate can redo the test (replay the sentence + confirm yes/no).
    // Guarded against overlapping runs if the Retry button is clicked rapidly.
    const retryCheck = useCallback(async (id) => {
        if (retryRunningRef.current) return;
        retryRunningRef.current = true;
        try {
            if (id === "camera" || id === "microphone") {
                cleanupResources();
                await runCameraAndMicCheck();
            } else if (id === "browser") await runBrowserCheck();
            else if (id === "device") await runDeviceCheck();
            else if (id === "permissions") await runPermissionStateCheck();
            else if (id === "network") await runNetworkCheck();
            else if (id === "backend") await runBackendCheck();
            else if (id === "speaker") {
                setSpeakerConfirmed(null);
                setSpeakerPlaying(false);
                setCheck("speaker", { status: CHECK_STATUS.PENDING, message: "" });
            }
        } finally {
            retryRunningRef.current = false;
        }
    }, [cleanupResources, runBrowserCheck, runDeviceCheck, runPermissionStateCheck, runCameraAndMicCheck, runNetworkCheck, runBackendCheck, setCheck]);

    // Checks only start running once the candidate reaches Step 2 (after agreeing
    // to terms and clicking Continue) — matches the step-based reference flow.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            cleanupResources();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (step !== 2) return undefined;
        if (runGuardRef.current) return undefined; // StrictMode double-invoke guard
        runGuardRef.current = true;
        runAllChecks();
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // Speaker test now speaks an actual sentence (via the browser's built-in
    // text-to-speech) instead of playing a short tone, so the candidate is
    // confirming they can hear real, intelligible speech.
    const playTestSound = useCallback(() => {
        try {
            if (!window.speechSynthesis) {
                console.error("System check: speechSynthesis not supported for speaker test");
                setSpeakerPlaying(false);
                return;
            }
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(SPEAKER_TEST_SENTENCE);
            utterance.rate = 0.95;
            utterance.onstart = () => setSpeakerPlaying(true);
            utterance.onend = () => setSpeakerPlaying(false);
            utterance.onerror = (err) => {
                console.error("System check: speaker test speech error", err);
                setSpeakerPlaying(false);
            };
            window.speechSynthesis.speak(utterance);
        } catch (err) {
            console.error("System check: unable to play test sound", err);
            setSpeakerPlaying(false);
        }
    }, []);

    // Reflect speaker confirmation into the checks map (not auto-passed on playback alone)
    useEffect(() => {
        if (speakerConfirmed === true) {
            setCheck("speaker", { status: CHECK_STATUS.PASSED, message: "Speaker confirmed." });
        } else if (speakerConfirmed === false) {
            setCheck("speaker", {
                status: CHECK_STATUS.FAILED,
                message: "Check your system volume and output device, then replay the test sentence.",
            });
        }
    }, [speakerConfirmed, setCheck]);

    // ---- Verification photo: face-presence check + brightness check -------
    // NOT identity matching. Confirms (a) a real face is actually in frame —
    // so a hand, empty desk, or an out-of-frame candidate is rejected — and
    // (b) the frame isn't too dark to be usable.
    const captureSelfie = useCallback(async () => {
        const video = videoRef.current;
        if (!video || !streamRef.current) return;
        const canvas = canvasRef.current || document.createElement("canvas");
        canvasRef.current = canvas;
        const w = video.videoWidth || 640;
        const h = video.videoHeight || 480;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, w, h);

        setSelfieStatus("checking");

        let avgBrightness = 255; // assume bright enough if we can't read pixels (e.g. CORS-restricted)
        try {
            const frame = ctx.getImageData(0, 0, w, h).data;
            let sum = 0;
            const step = 16; // sample a subset of pixels for performance
            let count = 0;
            for (let i = 0; i < frame.length; i += step) {
                sum += (frame[i] + frame[i + 1] + frame[i + 2]) / 3;
                count++;
            }
            avgBrightness = count > 0 ? sum / count : 255;
        } catch (err) {
            // getImageData can throw in rare tainted-canvas cases — treat as pass
            console.error("System check: unable to read selfie pixel data for brightness check", err);
        }

        // Face presence check. Skips (fails open) if the model never loaded,
        // so a model/network problem never permanently blocks the candidate.
        const faceDetected = faceModelsLoaded ? await detectFaceInImage(canvas) : true;

        if (!mountedRef.current) return;

        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setSelfieDataUrl(dataUrl);

        if (!faceDetected) {
            setSelfieStatus("no_face");
        } else if (avgBrightness < MIN_SELFIE_BRIGHTNESS) {
            setSelfieStatus("too_dark");
        } else {
            setSelfieStatus("captured");
        }
    }, [faceModelsLoaded]);

    const retakeSelfie = useCallback(() => {
        setSelfieDataUrl(null);
        setSelfieStatus("idle");
    }, []);

    const handleStartInterview = useCallback(() => {
        // Release all temporary resources BEFORE handing control to the existing interview,
        // so it can acquire its own camera/mic exactly as it does today.
        cleanupResources();
        onPassed && onPassed();
    }, [cleanupResources, onPassed]);

    const handleRunAllAgain = useCallback(() => {
        runAllChecks();
    }, [runAllChecks]);

    const checklist = CHECK_DEFINITIONS.map((c) => checks[c.id]);
    const completedCount = checklist.filter((c) => c.status === CHECK_STATUS.PASSED || c.status === CHECK_STATUS.WARNING).length;
    const anyBlockingFailed = checklist.some((c) => c.blocking && c.status === CHECK_STATUS.FAILED);

    // Speaker sitting at initial PENDING (before candidate clicks Play/Yes/No) does
    // NOT count toward anyChecking — it's user-driven, not part of the automatic run.
    const anyChecking = checklist.some((c) => {
        if (c.id === "speaker") return c.status === CHECK_STATUS.CHECKING;
        return c.status === CHECK_STATUS.CHECKING || c.status === CHECK_STATUS.PENDING;
    });

    const speakerNeedsConfirmation = checks.speaker.status === CHECK_STATUS.PENDING || checks.speaker.status === CHECK_STATUS.CHECKING;
    const selfieNeeded = checks.camera.status === CHECK_STATUS.PASSED && selfieStatus !== "captured";
    const canStart = !anyChecking && !anyBlockingFailed && !speakerNeedsConfirmation && !selfieNeeded;

    const selfieShowingPhoto = selfieStatus === "captured" || selfieStatus === "too_dark" || selfieStatus === "no_face";

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            disableEscapeKeyDown
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: "94vh",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
                },
            }}
        >
            {/* Header */}
            <Box sx={{ px: { xs: 2.5, md: 4 }, py: 2.5, borderBottom: "1px solid #e5e7eb" }}>
                <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: 22, md: 26 } }}>System Check</Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25 }}>
                    Make sure your device is ready before starting the interview. Usually takes less than a minute.
                </Typography>
            </Box>

            {/* Step indicator */}
            <Box sx={{ display: "flex", alignItems: "center", px: { xs: 2.5, md: 4 }, pt: 2.5, pb: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#22c55e", flexShrink: 0 }} />
                <Box sx={{ flex: 1, height: 2, bgcolor: step === 2 ? "#22c55e" : "#e5e7eb", mx: 1 }} />
                <Box sx={{
                    width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                    bgcolor: step === 2 ? "#22c55e" : "#fff",
                    border: step === 2 ? "none" : "2px solid #cbd5e1",
                }} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", px: { xs: 2.5, md: 4 }, pb: 2 }}>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>Terms</Typography>
                <Typography variant="caption" sx={{ color: step === 2 ? "#16a34a" : "#94a3b8", fontWeight: 600 }}>System Check + Verification Photo</Typography>
            </Box>

            {/* STEP 1 — Terms */}
            {step === 1 && (
                <Box sx={{ px: { xs: 2.5, md: 4 }, pb: 4 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: ACCENT_BLUE, mb: 1.5, fontSize: { xs: 17, md: 19 } }}>
                        About Online Proctoring{candidateName ? ` — ${candidateName}` : ""}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: "#334155" }}>
                        This is a proctored interview. We'll quickly check that your camera, microphone, browser,
                        and internet connection are ready, and capture a verification photo before you begin.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
                        {[
                            "Your webcam and microphone will be used only for this readiness check.",
                            "No video or audio from this step is recorded or stored.",
                            "You can retry any individual check that doesn't pass.",
                        ].map((t, i) => (
                            <Box key={i} sx={{ flex: "1 1 220px", p: 1.5, bgcolor: "#f8fafc", borderRadius: 1, fontSize: 13, color: "#475569", border: "1px solid #eef2f7" }}>
                                {t}
                            </Box>
                        ))}
                    </Box>
                    <FormControlLabel
                        control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
                        label={<Typography variant="body2">I agree to the terms above, and I'm ready to run the system check.</Typography>}
                    />
                </Box>
            )}

            {/* STEP 2 — System Check + Verification Photo */}
            {step === 2 && (
                <Box sx={{ px: { xs: 2.5, md: 4 }, pb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: ACCENT_BLUE, mb: 2, fontSize: { xs: 17, md: 19 } }}>
                        System Check + Verification Photo
                    </Typography>
                    <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
                        {/* Checklist column (left, boxed rows like the reference) */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
                                <Typography variant="body2" fontWeight={700} sx={{ color: "#334155" }}>System Check</Typography>
                                <Typography variant="caption" sx={{ color: "#64748b" }}>{completedCount} of {checklist.length}</Typography>
                            </Box>

                            {checklist.map((c) => {
                                const Icon = ICONS[c.id];
                                return (
                                    <Box
                                        key={c.id}
                                        sx={{
                                            display: "flex", alignItems: "center", justifyContent: "space-between",
                                            border: "1px solid #e5e7eb", borderRadius: 1, px: 1.5, py: 1.1, mb: 1.1,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", gap: 1.1, alignItems: "flex-start", minWidth: 0 }}>
                                            {Icon && <Icon sx={{ fontSize: 18, color: "#64748b", mt: "2px" }} />}
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13.5 }}>{c.label}</Typography>
                                                {c.id === "microphone" && (
                                                    <Box sx={{ height: 4, width: 90, bgcolor: "#e2e8f0", borderRadius: 2, mt: 0.5, overflow: "hidden" }}>
                                                        <Box sx={{ height: "100%", width: `${Math.min(100, micLevel)}%`, bgcolor: "#22c55e", transition: "width .15s linear" }} />
                                                    </Box>
                                                )}
                                                {c.message && (
                                                    <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.25, fontSize: 11 }}>
                                                        {c.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0, ml: 1 }}>
                                            <StatusBadge status={c.status} />
                                            {c.retryable && (c.status === CHECK_STATUS.FAILED || c.status === CHECK_STATUS.WARNING) && (
                                                <Tooltip title="Retry this check">
                                                    <IconButton size="small" onClick={() => retryCheck(c.id)} aria-label={`Retry ${c.label} check`}>
                                                        <ReplayIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}

                            {/* Speaker test, boxed like the other rows */}
                            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, px: 1.5, py: 1.25, mb: 1.1 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13.5 }}>Speaker Test</Typography>
                                    <StatusBadge status={checks.speaker.status} />
                                </Box>
                                <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                                    <Button size="small" variant="outlined" onClick={playTestSound} disabled={speakerPlaying} startIcon={<VolumeUpIcon sx={{ fontSize: 16 }} />}>
                                        {speakerPlaying ? "Playing…" : "Play Test Sentence"}
                                    </Button>
                                    <Button size="small" variant="contained" onClick={() => setSpeakerConfirmed(true)} sx={{ bgcolor: BRAND_BLUE + " !important" }}>
                                        Yes, I hear it
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setSpeakerConfirmed(false)}
                                        sx={{ fontWeight: 600, borderWidth: 1.5, "&:hover": { borderWidth: 1.5 } }}
                                    >
                                        No
                                    </Button>
                                </Box>
                                {speakerConfirmed === false && (
                                    <Typography variant="caption" sx={{ color: "#dc2626", display: "block", mt: 1 }}>
                                        Check your system volume and output device, then replay the test sentence.
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                <CircularProgress size={14} sx={{ display: anyChecking ? "inline-flex" : "none" }} />
                                <Typography variant="caption" sx={{ color: "#64748b" }}>{activeLabel}</Typography>
                            </Box>

                            <Button
                                fullWidth
                                variant="text"
                                size="small"
                                onClick={handleRunAllAgain}
                                disabled={anyChecking}
                                sx={{ mt: 1.25, textTransform: "none" }}
                            >
                                Run all checks again
                            </Button>

                            <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: ACCENT_BLUE }}>
                                Please use the same mic &amp; camera device during the interview.
                            </Typography>
                        </Box>

                        {/* Camera preview + verification photo (right) */}
                        <Box sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0 }}>
                            <Box sx={{
                                position: "relative", width: "100%", aspectRatio: "4/3", bgcolor: "#000", borderRadius: 1,
                                overflow: "hidden", border: "2px solid " + (checks.camera.status === CHECK_STATUS.PASSED ? "#22c55e" : "#e5e7eb"),
                                display: selfieShowingPhoto ? "none" : "block",
                            }}>
                                <video ref={videoRef} autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                {checks.camera.status !== CHECK_STATUS.PASSED && (
                                    <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <VideocamOffIcon sx={{ color: "#fff", opacity: 0.5, fontSize: 40 }} />
                                    </Box>
                                )}
                            </Box>

                            {selfieShowingPhoto && selfieDataUrl && (
                                <Box sx={{
                                    width: "100%", aspectRatio: "4/3", borderRadius: 1, overflow: "hidden",
                                    border: "2px solid " + (selfieStatus === "captured" ? "#22c55e" : "#ef4444"),
                                    backgroundImage: `url(${selfieDataUrl})`, backgroundSize: "cover", backgroundPosition: "center",
                                }} />
                            )}

                            {selfieStatus === "no_face" && (
                                <Typography variant="caption" sx={{ color: "#ef4444", display: "block", mt: 1 }}>
                                    We couldn't detect a clear face in the photo. Please make sure your face isn't covered or out of frame, then retake it.
                                </Typography>
                            )}
                            {selfieStatus === "too_dark" && (
                                <Typography variant="caption" sx={{ color: "#ef4444", display: "block", mt: 1 }}>
                                    Please take the picture again. Please ensure adequate light on your face.
                                </Typography>
                            )}
                            {selfieStatus === "captured" && (
                                <Typography variant="caption" sx={{ color: "#16a34a", display: "block", mt: 1 }}>
                                    Verification photo captured.
                                </Typography>
                            )}

                            <Box sx={{ mt: 1.5 }}>
                                {selfieStatus === "idle" && (
                                    <Button
                                        fullWidth variant="outlined" startIcon={<PhotoCameraIcon />}
                                        onClick={captureSelfie}
                                        disabled={checks.camera.status !== CHECK_STATUS.PASSED || !faceCheckAttempted}
                                        sx={{ textTransform: "none", borderColor: ACCENT_BLUE, color: ACCENT_BLUE }}
                                    >
                                        {faceCheckAttempted ? "Click a Selfie" : "Preparing face check…"}
                                    </Button>
                                )}
                                {selfieStatus === "checking" && (
                                    <Button fullWidth variant="outlined" disabled sx={{ textTransform: "none" }}>
                                        <CircularProgress size={16} sx={{ mr: 1 }} /> Checking photo…
                                    </Button>
                                )}
                                {selfieShowingPhoto && (
                                    <Button
                                        fullWidth variant="outlined" onClick={retakeSelfie}
                                        sx={{ textTransform: "none", borderColor: ACCENT_BLUE, color: ACCENT_BLUE }}
                                    >
                                        Retake Selfie
                                    </Button>
                                )}
                            </Box>

                            {speakerNeedsConfirmation && (
                                <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#94a3b8", textAlign: "center" }}>
                                    Please confirm the speaker test to continue.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Footer actions */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: { xs: 2.5, md: 4 }, py: 2.5, borderTop: "1px solid #e5e7eb" }}>
                {step === 1 && (
                    <Button
                        variant="contained"
                        disabled={!agreed}
                        onClick={() => setStep(2)}
                        sx={{ textTransform: "none", px: 4, py: 1.2, bgcolor: BRAND_BLUE + " !important", fontWeight: "bold" }}
                    >
                        Continue
                    </Button>
                )}
                {step === 2 && (
                    <Button
                        variant="contained"
                        size="large"
                        disabled={!canStart}
                        onClick={handleStartInterview}
                        sx={{
                            px: 4, py: 1.2, bgcolor: BRAND_BLUE + " !important", textTransform: "none",
                            fontWeight: "bold", "&:disabled": { bgcolor: "#cbd5e1 !important", color: "#64748b" },
                        }}
                    >
                        Proceed to Interview
                    </Button>
                )}
            </Box>
        </Dialog>
    );
}